import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  CommandPanel,
} from '@/components/ui/command';
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Kbd } from '@/components/ui/kbd';
import { Label } from '@/components/ui/label';
import { OTPField, OTPFieldInput } from '@/components/ui/otp-field';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsPanel, TabsTab } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipPopup, TooltipTrigger } from '@/components/ui/tooltip';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/ui-sandbox')({
  component: UISandbox,
});

function UISandbox() {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[var(--color-paper-alt)] p-8">
      <header className="mb-8">
        <h1 className="text-2xl font-semibold">dashboard-v2 / UI sandbox</h1>
        <p className="text-sm text-muted-foreground">
          Every primitive the plan pins. Light mode only — toggle{' '}
          <Kbd>prefers-color-scheme: dark</Kbd> in devtools and confirm nothing changes.
        </p>
      </header>

      <div className="mx-auto grid max-w-6xl gap-6">
        <Section title="Buttons">
          <div className="flex flex-wrap gap-3">
            <Button>Default</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="destructive">Destructive</Button>
            <Button variant="link">Link</Button>
            <Button disabled>Disabled</Button>
          </div>
        </Section>

        <Section title="Badges">
          <div className="flex flex-wrap gap-2">
            <Badge>Default</Badge>
            <Badge variant="secondary">Secondary</Badge>
            <Badge variant="outline">Outline</Badge>
            <Badge variant="destructive">Destructive</Badge>
          </div>
        </Section>

        <Section title="Inputs & Forms">
          <Field className="max-w-sm">
            <FieldLabel>Email</FieldLabel>
            <Input type="email" placeholder="you@example.com" />
          </Field>
          <Field className="max-w-sm">
            <FieldLabel>Password</FieldLabel>
            <Input type="password" aria-invalid />
            <FieldError>Too short</FieldError>
          </Field>
          <Field className="max-w-sm">
            <FieldLabel>Bio</FieldLabel>
            <Textarea placeholder="Tell us about yourself" />
          </Field>
          <div className="flex items-center gap-6">
            <Label className="flex items-center gap-2">
              <Checkbox defaultChecked />
              Accept terms
            </Label>
            <Label className="flex items-center gap-2">
              <Switch defaultChecked />
              Notifications
            </Label>
          </div>
        </Section>

        <Section title="Select">
          <div className="max-w-sm">
            <Select>
              <SelectTrigger>
                <SelectValue placeholder="Pick a framework" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="react">React</SelectItem>
                <SelectItem value="svelte">Svelte</SelectItem>
                <SelectItem value="vue">Vue</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Section>

        <Section title="OTP Field">
          <OTPField aria-label="Verification code" length={6}>
            <OTPFieldInput aria-label="Character 1 of 6" />
            <OTPFieldInput aria-label="Character 2 of 6" />
            <OTPFieldInput aria-label="Character 3 of 6" />
            <OTPFieldInput aria-label="Character 4 of 6" />
            <OTPFieldInput aria-label="Character 5 of 6" />
            <OTPFieldInput aria-label="Character 6 of 6" />
          </OTPField>
        </Section>

        <Section title="Cards">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>Long-content card</CardTitle>
              <CardDescription>Demonstrates typography inside a Card.</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                The quick brown fox jumps over the lazy dog. The quick brown fox jumps over the lazy
                dog. The quick brown fox jumps over the lazy dog.
              </p>
            </CardContent>
          </Card>
        </Section>

        <Section title="Breadcrumb">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="#">Settings</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="#">Developer</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>API keys</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </Section>

        <Section title="Tabs">
          <Tabs defaultValue="overview" className="max-w-md">
            <TabsList>
              <TabsTab value="overview">Overview</TabsTab>
              <TabsTab value="timeline">Timeline</TabsTab>
              <TabsTab value="raw">Raw</TabsTab>
            </TabsList>
            <TabsPanel value="overview">Overview content.</TabsPanel>
            <TabsPanel value="timeline">Timeline content.</TabsPanel>
            <TabsPanel value="raw">Raw content.</TabsPanel>
          </Tabs>
        </Section>

        <Section title="Overflowing table">
          <ScrollArea className="h-64 rounded-md border">
            <Table>
              <TableCaption>Example data.</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>Column A</TableHead>
                  <TableHead>Column B</TableHead>
                  <TableHead>Column C</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 20 }, (_, i) => i + 1).map((rowNum) => (
                  <TableRow key={`row-${rowNum}`}>
                    <TableCell>Row {rowNum} A</TableCell>
                    <TableCell>Row {rowNum} B</TableCell>
                    <TableCell>Row {rowNum} C</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </Section>

        <Section title="Alert + Progress">
          <Alert>
            <AlertTitle>Heads up</AlertTitle>
            <AlertDescription>This is an informational alert.</AlertDescription>
          </Alert>
          <div className="max-w-sm space-y-2">
            <Label>Progress 45%</Label>
            <Progress value={45} />
          </div>
        </Section>

        <Section title="Avatars & skeletons">
          <div className="flex items-center gap-4">
            <Avatar>
              <AvatarFallback>PG</AvatarFallback>
            </Avatar>
            <Avatar>
              <AvatarFallback>WV</AvatarFallback>
            </Avatar>
            <Separator orientation="vertical" className="h-8" />
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-8 w-8 rounded-full" />
          </div>
        </Section>

        <Section title="Dialog">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger render={<Button variant="outline">Open dialog</Button>} />
            <DialogPopup>
              <DialogHeader>
                <DialogTitle>Example dialog</DialogTitle>
                <DialogDescription>Body text goes here.</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose render={<Button variant="outline">Cancel</Button>} />
                <DialogClose render={<Button>Confirm</Button>} />
              </DialogFooter>
            </DialogPopup>
          </Dialog>
        </Section>

        <Section title="Command (standalone)">
          <CommandPanel className="max-w-md">
            <Command
              items={[
                { value: 'linear', label: 'Linear' },
                { value: 'figma', label: 'Figma' },
                { value: 'slack', label: 'Slack' },
              ]}
            >
              <CommandInput placeholder="Search…" />
              <CommandEmpty>No results</CommandEmpty>
              <CommandList>
                {(item: { value: string; label: string }) => (
                  <CommandItem key={item.value} value={item.value}>
                    {item.label}
                  </CommandItem>
                )}
              </CommandList>
            </Command>
          </CommandPanel>
        </Section>

        <Section title="Tooltip">
          <Tooltip>
            <TooltipTrigger render={<Button variant="outline">Hover me</Button>} />
            <TooltipPopup>Helpful hint</TooltipPopup>
          </Tooltip>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</h2>
      <div className="space-y-3 rounded-lg border bg-card p-4">{children}</div>
    </section>
  );
}
